/**
 * _sheet_motion.js — the in-app chat panel's close animation and drag-to-dismiss.
 *
 * Opening needs no script: ai_chat.css plays the grow-in keyframes as soon as
 * the panel stops being `hidden`. Closing does, because `hidden` removes the
 * panel at once; closeSheet() holds the open state while the shrink plays,
 * then runs the caller's real close. A reopen mid-shrink cancels it
 * (cancelClose), so a quick close-open never ends with a hidden panel.
 *
 * Drag: on the phone sheet the panel follows a downward drag that starts on
 * its grab handle or header, or in its transcript while that is scrolled to
 * the top (the way a native sheet hands an overscroll to the sheet). Past the
 * threshold, or flicked, it slides off and closes; otherwise it springs back.
 * The physics are gestures.js, shared with sheet_controller.js (the Bootstrap
 * modal sheets), so both sheets resist, arm and buzz alike; the tuning stays
 * here. Starting a drag drops the keyboard, so a dismiss never strands it.
 *
 * Window-level move/up listeners, like sheet_controller.js: a finger that
 * leaves the panel mid-drag keeps driving it, and no pointer capture is taken
 * (a capture on a tab button would retarget the click the drag must swallow).
 *
 * Imported by ai_chat_controller.js.
 */
import { bump, crossedThreshold, lockAxis, motionToken, rubberBand } from "../../gestures.js";

const CLOSING_CLASS = 'pchat-panel--closing';
// Longer than the shrink keyframes and the slide-off below, so a missed
// animationend/transitionend (animations disabled, panel already gone) still
// finishes the close.
const CLOSE_FALLBACK_MS = 300;

const AXIS_DEADZONE = 6;        // px before committing to an axis (sheet_controller.js)
const DISMISS_MIN_PX = 90;      // absolute floor before a release dismisses...
const DISMISS_FRACTION = 0.25;  // ...or this fraction of the sheet's height
const FLICK_VELOCITY = 0.6;     // px/ms: a quick flick dismisses below the threshold
const RESIST = 160;             // an upward drag has nowhere to go; damp it hard
const RESIST_SCALE = 0.3;
// iOS delivers the click that follows a drag some time after touchend.
const CLICK_SWALLOW_MS = 400;

// Where a drag may start. The handle and header always; a transcript only
// while it sits at its top, and never from a field being typed into.
const GRIP = '.sheet-handle, .pchat-header';
const SCROLLER = '.pchat-body';
const TYPING = 'input, textarea, select, [contenteditable="true"]';

function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Play a one-shot CSS animation class on `element`, restarting it if it is
 * already running, and drop the class afterwards so nothing replays it later
 * (an element that goes display:none and back restarts its animation).
 * Used for the tab fade (chat_tabs_controller.js) and the launcher's return.
 */
export function playOnce(element, className, durationMs) {
    element.classList.remove(className);
    void element.offsetWidth;   // commit the removal so re-adding restarts it
    element.classList.add(className);
    clearTimeout(element._playOnceTimer);
    element._playOnceTimer = setTimeout(() => element.classList.remove(className), durationMs);
}

/** Play the shrink on `panel`, then call `done`. Immediate under reduced motion or when already hidden. */
export function closeSheet(panel, done) {
    cancelClose(panel);
    if (reducedMotion() || panel.hidden) { done(); return; }
    finishOnce(panel, 'animationend', done);
    panel.classList.add(CLOSING_CLASS);
}

/** Whether `panel` is playing its shrink. */
export function isClosing(panel) {
    return panel.classList.contains(CLOSING_CLASS);
}

/** Stop a shrink or slide in progress without closing (a reopen, or the close finishing). */
export function cancelClose(panel) {
    if (panel._sheetCancel) panel._sheetCancel();
    panel.classList.remove(CLOSING_CLASS);
}

// Run `done` once, on `eventName` or the fallback timer, whichever comes first,
// and leave no listener behind: a stray one would close the panel on some
// later, unrelated transition.
function finishOnce(panel, eventName, done) {
    const onEvent = (event) => { if (event.target === panel) finish(); };
    const cancel = () => {
        clearTimeout(timer);
        panel.removeEventListener(eventName, onEvent);
        panel._sheetCancel = null;
    };
    const finish = () => { cancel(); panel.classList.remove(CLOSING_CLASS); done(); };
    const timer = setTimeout(finish, CLOSE_FALLBACK_MS);
    panel.addEventListener(eventName, onEvent);
    panel._sheetCancel = cancel;
}

/**
 * Let a downward drag dismiss `panel` while `isSheet()` is true. `onDismiss`
 * closes without the shrink (the panel has already slid off). Returns a
 * function that removes the listeners.
 */
export function attachSwipeDismiss(panel, isSheet, onDismiss) {
    let drag = null;

    const onDown = (event) => {
        if (!isSheet() || event.button > 0 || panel.hidden) return;
        const origin = dragOrigin(panel, event.target);
        if (!origin) return;
        drag = {
            id: event.pointerId, origin, axis: null, armed: false,
            x: event.clientX, y: event.clientY, t: event.timeStamp, dy: 0, velocity: 0,
            height: panel.offsetHeight || 1,
        };
    };

    const onMove = (event) => {
        if (!drag || event.pointerId !== drag.id) return;
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;
        if (drag.axis === null && !lockDrag(panel, drag, dx, dy)) return;
        if (drag.axis !== 'v') return;
        event.preventDefault();
        follow(panel, drag, dy, event.timeStamp);
    };

    const onUp = (event) => {
        if (!drag || event.pointerId !== drag.id) return;
        const ended = drag;
        drag = null;
        if (ended.axis !== 'v') return;
        swallowNextClick(panel);
        if (ended.armed || ended.velocity > FLICK_VELOCITY) slideOff(panel, onDismiss);
        else springBack(panel);
    };

    panel.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
        panel.removeEventListener('pointerdown', onDown);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
    };
}

// 'grip' for the handle and header; the scroller for a transcript at its top;
// null where a drag must stay the browser's (a field, a scrolled transcript).
function dragOrigin(panel, target) {
    if (!target.closest || target.closest(TYPING)) return null;
    if (target.closest(GRIP)) return 'grip';
    const scroller = target.closest(SCROLLER);
    return scroller && panel.contains(scroller) && scroller.scrollTop <= 0 ? scroller : null;
}

// Commit to an axis at the deadzone edge. A transcript drag only becomes the
// sheet's when it heads down from the very top; anything else is a scroll.
function lockDrag(panel, drag, dx, dy) {
    const axis = lockAxis(dx, dy, AXIS_DEADZONE);
    if (axis === null) return false;
    const fromTranscript = drag.origin !== 'grip';
    // 'off': the gesture belongs to the browser (a sideways pan or a scroll).
    drag.axis = axis === 'v' && (!fromTranscript || (dy > 0 && drag.origin.scrollTop <= 0)) ? 'v' : 'off';
    if (drag.axis !== 'v') return true;
    if (panel.contains(document.activeElement)) document.activeElement.blur();
    panel.style.transition = 'none';
    return true;
}

function follow(panel, drag, dy, timeStamp) {
    drag.velocity = dy > drag.dy ? (dy - drag.dy) / Math.max(1, timeStamp - drag.t) : 0;
    drag.dy = dy;
    drag.t = timeStamp;
    // Downward tracks the finger 1:1; upward resists rather than lifting the
    // sheet off the bottom of the screen.
    const offset = dy >= 0 ? dy : rubberBand(dy, RESIST, RESIST_SCALE);
    panel.style.transform = `translateY(${offset}px)`;
    const armed = dy > 0 && crossedThreshold(dy, DISMISS_MIN_PX, DISMISS_FRACTION, drag.height);
    if (armed !== drag.armed) {
        drag.armed = armed;
        if (armed) bump();   // one pulse as it crosses into "release to close"
    }
}

function slideOff(panel, onDismiss) {
    panel.style.transition = `transform var(--dur-base) ${motionToken('--ease-standard')}`;
    panel.style.transform = 'translateY(100%)';
    finishOnce(panel, 'transitionend', () => {
        onDismiss();
        clearDrag(panel);
    });
}

function springBack(panel) {
    panel.style.transition = `transform var(--dur-base) ${motionToken('--ease-settle')}`;
    panel.style.transform = '';
    finishOnce(panel, 'transitionend', () => clearDrag(panel));
}

function clearDrag(panel) {
    panel.style.transition = '';
    panel.style.transform = '';
}

// The pointerup that ends a drag is followed by a click on whatever the drag
// started over (a tab, a chip); swallow that one click.
function swallowNextClick(panel) {
    const swallow = (event) => { event.stopPropagation(); event.preventDefault(); };
    panel.addEventListener('click', swallow, { capture: true, once: true });
    setTimeout(() => panel.removeEventListener('click', swallow, { capture: true }), CLICK_SWALLOW_MS);
}
